import React, { useEffect, useState } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Leaf, Cloud, TreePine, Car, Bike, Truck } from "lucide-react";
import API_BASE_URL from "../api";

// ⚠️ Adjust to match your real backend route once it exists.
const API_BASE = `${API_BASE_URL}/api/impact`;

function ImpactCard({ icon, iconBg, label, value, unit, loading }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 flex flex-col gap-2 shadow-sm">
      <div
        className="w-10 h-10 rounded-full flex items-center justify-center"
        style={{ backgroundColor: iconBg }}
      >
        {icon}
      </div>
      {loading ? (
        <span className="h-7 w-24 bg-gray-100 rounded animate-pulse" />
      ) : (
        <span className="text-2xl font-bold text-gray-900">
          {value} {unit && <span className="text-sm font-normal text-gray-400">{unit}</span>}
        </span>
      )}
      <span className="text-sm text-gray-500">{label}</span>
    </div>
  );
}

export default function AdminImpact() {
  const [summary, setSummary] = useState({
    totalCarbonSaved: null,
    totalTreesEquivalent: null,
    totalFuelSaved: null,
  });
  const [summaryLoading, setSummaryLoading] = useState(true);

  const [trend, setTrend] = useState([]);
  const [trendLoading, setTrendLoading] = useState(true);

  const [byCategory, setByCategory] = useState([]);
  const [categoryLoading, setCategoryLoading] = useState(true);

  useEffect(() => {
    const fetchSummary = async () => {
      setSummaryLoading(true);
      try {
        // ⚠️ Placeholder endpoint — replace with your real impact-summary route.
        const res = await fetch(`${API_BASE}/summary`).then((r) => r.json());
        setSummary({
          totalCarbonSaved: res?.totalCarbonSaved ?? 0,
          totalTreesEquivalent: res?.totalTreesEquivalent ?? 0,
          totalFuelSaved: res?.totalFuelSaved ?? 0,
        });
      } catch (err) {
        console.error("Failed to load impact summary:", err);
      } finally {
        setSummaryLoading(false);
      }
    };

    const fetchTrend = async () => {
      setTrendLoading(true);
      try {
        // ⚠️ Placeholder endpoint — replace with your real impact-trend route.
        const res = await fetch(`${API_BASE}/monthlyTrend`).then((r) => r.json());
        setTrend(res?.trend || []);
      } catch (err) {
        console.error("Failed to load impact trend:", err);
      } finally {
        setTrendLoading(false);
      }
    };

    const fetchCategory = async () => {
      setCategoryLoading(true);
      try {
        // ⚠️ Placeholder endpoint — replace with your real category-breakdown route.
        const res = await fetch(`${API_BASE}/byVehicleCategory`).then((r) => r.json());
        setByCategory(res?.categories || []);
      } catch (err) {
        console.error("Failed to load category breakdown:", err);
      } finally {
        setCategoryLoading(false);
      }
    };

    fetchSummary();
    fetchTrend();
    fetchCategory();
  }, []);

  return (
    <div className="space-y-5">
      <div>
        <h3 className="text-lg font-bold text-gray-900">Environmental Impact</h3>
        <p className="text-sm text-gray-500">Aggregate carbon savings across all registered vehicles</p>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-5">
        <ImpactCard
          icon={<Cloud size={18} className="text-blue-600" />}
          iconBg="#E3F1FE"
          label="Total CO₂ Saved"
          value={summary.totalCarbonSaved?.toLocaleString() ?? "—"}
          unit="Kg"
          loading={summaryLoading}
        />
        <ImpactCard
          icon={<TreePine size={18} className="text-emerald-700" />}
          iconBg="#E8F5E9"
          label="Equivalent Trees Planted"
          value={summary.totalTreesEquivalent?.toLocaleString() ?? "—"}
          loading={summaryLoading}
        />
        <ImpactCard
          icon={<Leaf size={18} className="text-amber-600" />}
          iconBg="#FDF3E3"
          label="Total Fuel Saved"
          value={summary.totalFuelSaved?.toLocaleString() ?? "—"}
          unit="L"
          loading={summaryLoading}
        />
      </div>

      {/* Trend + category breakdown */}
      <div className="grid grid-cols-12 gap-5">
        <div className="col-span-8 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h4 className="font-semibold text-gray-900 mb-4">CO₂ Saved Over Time</h4>
          {trendLoading ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
              Loading trend…
            </div>
          ) : trend.length === 0 ? (
            <div className="h-[260px] flex items-center justify-center text-sm text-gray-400">
              No impact data yet
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={trend} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="impactFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2E7D32" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="#2E7D32" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F0F0F0" />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#9AA0A6" }} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #F0F0F0", fontSize: 12 }} />
                <Area
                  type="monotone"
                  dataKey="carbonSaved"
                  stroke="#2E7D32"
                  strokeWidth={2.5}
                  fill="url(#impactFill)"
                  dot={{ r: 3, fill: "#2E7D32", strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="col-span-4 bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
          <h4 className="font-semibold text-gray-900 mb-4">Impact by Vehicle Category</h4>
          {categoryLoading ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">
              Loading…
            </div>
          ) : byCategory.length === 0 ? (
            <div className="h-[220px] flex items-center justify-center text-sm text-gray-400">
              No data yet
            </div>
          ) : (
            <div className="space-y-4">
              {byCategory.map((cat) => {
                const Icon = cat.category === "Cars" ? Car : cat.category === "Bikes" ? Bike : Truck;
                return (
                  <div key={cat.category} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-gray-600">
                      <Icon size={15} className="text-emerald-700" />
                      {cat.category}
                    </span>
                    <span className="font-semibold text-gray-800">
                      {cat.carbonSaved?.toLocaleString() ?? 0} Kg
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}